import { useEffect } from 'react';
import { useRoute } from '@/hooks/useRoute';
import { trackMielleyEvent } from '@/lib/mielleyAnalytics';

const PAGE_DEDUPE_KEY='mielley_analytics_last_page_v1';
const SEARCH_ROUTE_DEDUPE_KEY='mielley_analytics_last_route_search_v2';

const productSlugFromPath=(path:string)=>{
  const match=path.split('?')[0].match(/^\/product\/(.+)$/);
  return match?decodeURIComponent(match[1]):'';
};

const readCatalogResults=(query:string)=>{
  const node=document.querySelector<HTMLElement>('[data-mielley-search-term]');
  if(!node)return null;
  const nodeQuery=(node.dataset.mielleySearchTerm||'').trim();
  if(nodeQuery!==query.trim())return null;
  const raw=node.dataset.mielleyResultsCount||'';
  const count=Number(raw);
  return Number.isFinite(count)?count:null;
};

export function MielleyAnalyticsTracker(){
  const route=useRoute();

  useEffect(()=>{
    if(route.startsWith('/admin'))return;
    const now=Date.now();
    try{
      const raw=sessionStorage.getItem(PAGE_DEDUPE_KEY);
      const last=raw?JSON.parse(raw):null;
      if(last?.route===route&&now-Number(last?.at||0)<1500)return;
      sessionStorage.setItem(PAGE_DEDUPE_KEY,JSON.stringify({route,at:now}));
    }catch{}

    void trackMielleyEvent({eventName:'page_view',path:route});
    const slug=productSlugFromPath(route);
    if(slug)void trackMielleyEvent({eventName:'product_view',path:route,productSlug:slug});
 
    

    if(route.split('?')[0]==='/catalog'){
      const query=new URLSearchParams(route.split('?')[1]||'').get('q')?.trim()||'';
      if(query.length>=2){
        let duplicate=false;
        try{
          const raw=sessionStorage.getItem(SEARCH_ROUTE_DEDUPE_KEY);
          const last=raw?JSON.parse(raw):null;
          duplicate=last?.route===route&&now-Number(last?.at||0)<2500;
          if(!duplicate)sessionStorage.setItem(SEARCH_ROUTE_DEDUPE_KEY,JSON.stringify({route,at:now}));
        }catch{}

        if(!duplicate){
          let attempts=0;
          const readResults=()=>{
            attempts++;
            const count=readCatalogResults(query);
            if(count!==null){
              void trackMielleyEvent({
                eventName:'search',
                path:route,
                searchTerm:query,
                metadata:{results_count:count,source:'header_route_v2'},
              });
              return;
            }
            if(attempts<20)window.setTimeout(readResults,150);
          };
          window.setTimeout(readResults,100);
        }
      }
    }
  },[route]);

  useEffect(()=>{
    let searchTimer:number|undefined;

    const onInput=(event:Event)=>{
      const target=event.target;
      if(!(target instanceof HTMLInputElement))return;
      const placeholder=(target.placeholder||'').toLowerCase();
      const looksLikeSearch=Boolean(target.closest('.mp5-search'))||placeholder.includes('חיפוש')||placeholder.includes('search');
      if(!looksLikeSearch)return;
      if(searchTimer)window.clearTimeout(searchTimer);
      searchTimer=window.setTimeout(()=>{
        const value=target.value.trim();
        if(value.length<2)return;

        let attempts=0;
        const recordWhenReady=()=>{
          attempts++;
          const count=readCatalogResults(value);
          if(count!==null){
            void trackMielleyEvent({
              eventName:'search',
              path:window.location.pathname+window.location.search,
              searchTerm:value,
              metadata:{results_count:count,source:'catalog_input_v2'},
            });
            return;
          }
          if(attempts<12)window.setTimeout(recordWhenReady,120);
        };
        recordWhenReady();
      },900);
    };

    const onClick=(event:MouseEvent)=>{
      const target=event.target instanceof Element?event.target.closest('a,button'):null;
      if(!target)return;
      const text=(target.textContent||'').trim();
      const href=target instanceof HTMLAnchorElement?target.href:'';
      const path=window.location.pathname+window.location.search;
      const productSlug=productSlugFromPath(path);

      if(/הוספ.{0,4}לסל|add\s*to\s*cart/i.test(text)){
        void trackMielleyEvent({eventName:'add_to_cart',path,productSlug});
      }
      if(href.includes('wa.me')||/whatsapp/i.test(href)){
        void trackMielleyEvent({eventName:'whatsapp_click',path,productSlug});
      }
    };

    document.addEventListener('input',onInput,true);
    document.addEventListener('click',onClick,true);
    return()=>{
      if(searchTimer)window.clearTimeout(searchTimer);
      document.removeEventListener('input',onInput,true);
      document.removeEventListener('click',onClick,true);
    };
  },[]);

  return null;
}
